import { useQueryClient } from "@tanstack/react-query";
import { Navigate, useSearchParams, useNavigate } from "react-router-dom";
import TelehealthOverlay from "../components/TelehealthOverlay";
import { parseRoomParams } from "../utils/url";
import { clearActiveMeeting } from "../hooks/use-active-meeting";
import { useSnackbar } from "../../hooks/useSnackbar";
import { useUpdateAppointmentStatus } from "../../queries/use-change-appointment-status";
import { appPaths } from "../../constants/paths";
import {
  getErrorSnackbar,
  getPatientVisitNoteUrl,
  getXtenantId,
} from "../../utils/utils";
import { DobAuthProvider } from "../../layout/app/patient-portal/public-view/DobAuthProvider";

/** Returns true if the visitor has a provider-role session in localStorage.
 *  Host rejoin URLs are gated by this; the patient/public guest URL uses
 *  isPublicView instead. */
function hasProviderSession(): boolean {
  try {
    return (
      !!localStorage.getItem("access_token") &&
      localStorage.getItem("viewAs") === "provider"
    );
  } catch {
    return false;
  }
}

/** Returns true when the visitor is signed in to the patient portal. Used
 *  to skip the DOB challenge on the public guest route — a logged-in
 *  patient has already proven identity, so re-prompting would be friction. */
function hasPatientSession(): boolean {
  try {
    return (
      !!localStorage.getItem("access_token") &&
      localStorage.getItem("viewAs") === "patient"
    );
  } catch {
    return false;
  }
}

interface Props {
  isPublicView?: boolean;
}

/**
 * Route component mounted at /app/telehealth-v2/room (host) and
 * /public/telehealth-v2/join (guest). Designed to be loaded inside an
 * <iframe> as well as rendered standalone.
 *
 * When opened with role=host plus appointmentId/patientId in the URL, this
 * is the "host rejoin" entry point — used when the provider clicks the
 * Copy Host URL link from the Instant Appointment modal and pastes it into
 * a new tab. The same in-meeting Cancel / No Show / Start Encounter actions
 * are wired up here so the rejoin gives a full host experience, not just a
 * guest-style view of the room.
 */
export default function TelehealthRoomPage({ isPublicView = false }: Props) {
  const [search] = useSearchParams();
  const params = parseRoomParams(search);
  const navigate = useNavigate();
  const showSnackbar = useSnackbar();
  const queryClient = useQueryClient();
  const { mutateAsync: updateStatus } = useUpdateAppointmentStatus();

  // Auth gate — a host rejoin URL is provider-only. If the visitor isn't
  // signed in as a provider (logged out, or signed in as a patient/admin),
  // bounce them to the login page with a `next` param so they land back
  // here after a successful provider login. Prevents the Copy Host URL
  // link from working in unauthorised hands. The public guest route hits
  // this same component with isPublicView=true and is intentionally
  // exempt — patients shouldn't have to authenticate to join a visit.
  const requiresAuth = !isPublicView && params.role === "host";
  const authed = !requiresAuth || hasProviderSession();

  const wireHostBar =
    params.role === "host" && !!params.appointmentId && !!params.patientId;

  const onExit = () => {
    // Tell the embedding window we're done (no-op when standalone).
    try {
      window.parent?.postMessage({ type: "telehealth-v2:exit" }, "*");
    } catch {
      /* ignore */
    }
    window?.close();
  };

  const invalidateAppointments = () => {
    queryClient.invalidateQueries({ queryKey: ["appointment-list"] });
    queryClient.invalidateQueries({ queryKey: ["appointment-list-v2"] });
  };

  const updateEncounterState = async (status: "IN_EXAM" | "NO_SHOW" | "CANCELLED", reason?: string) => {

    if (!params.appointmentId || !params.patientId) return;
    try {
      await updateStatus({
        appointmentId: params.appointmentId,
        status: status,
        reason: reason,
        xTENANTID: getXtenantId(),
      });
      invalidateAppointments();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
      throw err;
    }
  }

  const handleCancelAppointment = async (reason: string) => {
    if (!params.appointmentId) return;
    try {
      await updateEncounterState("CANCELLED", reason);
      // Drop the global Rejoin pill — the meeting is over.
      clearActiveMeeting();
      onExit();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
      throw err;
    }
  };


  const handleNoShow = async () => {
    if (!params.appointmentId) return;
    try {
      await updateEncounterState("NO_SHOW");
      clearActiveMeeting();
      onExit();
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
      throw err;
    }
  };


  const handleStartEncounter = async () => {
    try {
      await updateEncounterState("IN_EXAM");
    } catch (err) {
      showSnackbar(getErrorSnackbar(err));
      throw err;
    }
    navigate(
      getPatientVisitNoteUrl(params.patientId ?? 0, params.appointmentId ?? 0)
    );
  };

  // Plain minimize from the rejoin route: navigate to the encounter screen
  // so the host has somewhere meaningful to look at while the meeting sits
  // in PiP. The overlay portals out of this route's tree, so on navigation
  // the meeting closes — this is the same trade-off the user accepted for
  // Start Encounter (don't break navigation).
  const handleMinimize = () => {
    if (!params.appointmentId || !params.patientId) return;
    navigate(getPatientVisitNoteUrl(params.patientId, params.appointmentId));
  };

  // Auth gate runs after hooks (Rules of Hooks: hooks always called in
  // the same order) but before the meeting overlay mounts. If we're not
  // a provider, bounce to login carrying the original URL.
  if (!authed) {
    const next = encodeURIComponent(
      window.location.pathname + window.location.search
    );
    return <Navigate to={`${appPaths.login}?next=${next}`} replace />;
  }


  // PiP is only meaningful when the visitor has somewhere to go in the
  // app while the call shrinks. Provider portal (isPublicView=false) and
  // logged-in patient portal users qualify; the open public guest URL
  // and the DOB-gated patient invite both land on a single-purpose page
  // with no other surface to navigate to, so we hide the affordance.
  const allowMiniMode = !isPublicView || hasPatientSession();
  
  // Render only the overlay — it portals to <body> and renders both the
  // full-screen meeting and the draggable PiP window itself, so the page
  // doesn't need its own wrapper.
  const overlay = (
    <TelehealthOverlay
      open
      params={params}
      isPublicView={isPublicView}
      onClose={onExit}
      providerName={params.role === "host" ? params.displayName : undefined}
      onCancelAppointment={wireHostBar ? handleCancelAppointment : undefined}
      onNoShow={wireHostBar ? handleNoShow : undefined}
      onStartEncounter={wireHostBar ? handleStartEncounter : undefined}
      onMinimize={wireHostBar ? handleMinimize : undefined}
      allowMiniMode={allowMiniMode}
    />
  );

  // Patient identity gate. The public guest route serves three flows:
  //   1. Open public link — no patientId in URL, anyone can join.
  //   2. Patient-specific invite (email/SMS) — patientId present, must
  //      verify DOB before the meeting renders.
  //   3. Logged-in patient portal user — patientId present but patient
  //      session already exists in localStorage, skip the challenge.
  // The DobAuthProvider reads patientId from the URL query string itself
  // and renders the ValidateDob screen until the API confirms.
  const requireDobGate =
    isPublicView && !!params.patientId && !hasPatientSession();
  if (requireDobGate) {
    return <DobAuthProvider>{overlay}</DobAuthProvider>;
  }
  return overlay;
}
